import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import BrickAreaFilter from "./filterModal/BrickAreaFilter";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { QUARTERS } from "../../../../common/constants/constant";
import { formatAmount } from "../../../../common/utils/CommonFunction";

// ================= TYPES =================
export type Quarter = "Q1" | "Q2" | "Q3" | "Q4" | null;

export interface QuarterMonthData {
  totalVisits: number;
  acps: number;
  secondaryOrders: number;
  invoices: number;
  months?: number[][]; // Array of 3 months, each containing visit data
}

export interface BrickWorkingRow {
  area: string;
  headquarter: string;
  brick: string;
  brickPotential: number;
  q1: QuarterMonthData;
  q2: QuarterMonthData;
  q3: QuarterMonthData;
  q4: QuarterMonthData;
}

type ColumnFilterKey = "area" | "headquarter" | "brick";
interface Props {
  rows: BrickWorkingRow[];
  allRows: BrickWorkingRow[];
  loading: boolean;
  onApplyFilter: (key: ColumnFilterKey, values: string[]) => void;
  columnFilters: Record<ColumnFilterKey, string[]>; // ✅ ADD
}

const MONTH_COL_WIDTH = 95;
const BRICK_COL_WIDTH = 100;

// ================= COMPONENT =================
const BrickWorkingReportExpandableTable: React.FC<Props> = ({
  rows,
  allRows,
  loading,
  onApplyFilter,
  columnFilters,
}) => {
  const [expandedQuarter, setExpandedQuarter] = useState<Quarter>(null);
  const [openAreaFilter, setOpenAreaFilter] = useState(false);
  const [openFilter, setOpenFilter] = useState<ColumnFilterKey | null>(null);

  const handleOpenAreaFilter = () => setOpenAreaFilter(true);
  const handleCloseAreaFilter = () => setOpenAreaFilter(false);

  const toggleQuarter = (q: Quarter) => {
    setExpandedQuarter((prev) => (prev === q ? null : q));
  };

  // Helper to get month names for each quarter
  const getMonthNames = (quarter: Quarter) => {
    if (!quarter) return [];
    return QUARTERS[quarter];
  };

  // Helper to get quarter range text
  const getQuarterRange = (quarter: string | null) => {
    switch (quarter) {
      case "Q1":
        return "Jan–Mar";
      case "Q2":
        return "Apr–Jun";
      case "Q3":
        return "Jul–Sep";
      case "Q4":
        return "Oct–Dec";
      default:
        return "";
    }
  };

  const quarters: Quarter[] = ["Q1", "Q2", "Q3", "Q4"];

  const SkeletonRow = ({ columns }: { columns: number }) => (
    <TableRow>
      {Array.from({ length: columns }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton
            variant="rectangular"
            height={16}
            animation="wave"
            sx={{ borderRadius: 1 }}
          />
        </TableCell>
      ))}
    </TableRow>
  );

  // ✅ PUT IT HERE ⬇️
  const filteredRows = useMemo(() => {
    let data = [...allRows];

    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        data = data.filter((row) =>
          values.includes(row[key as ColumnFilterKey])
        );
      }
    });

    return data;
  }, [allRows, columnFilters]);

  return (
    <TableContainer component={Paper} sx={{ mt: 2, overflowX: "auto" }}>
      <Table
        size="small"
        stickyHeader
        sx={{
          "& .MuiTableCell-root": {
            fontSize: "12px", // 🔽 smaller text
            padding: "4px 8px", // 🔽 compact row height
            lineHeight: 1.3,
          },

          "& .MuiTableCell-head": {
            fontSize: "12.5px", // headers slightly larger
            fontWeight: 600,
          },
        }}
      >
        <TableHead>
          {/* GROUP HEADER */}
          <TableRow>
            {/* LEFT BLUE HEADING */}
            <TableCell
              colSpan={4}
              sx={{
                ...groupHeader,
                textAlign: "left",
                pl: 2,
                borderRight: "2px solid #fff",
                fontSize: "14px",
              }}
            >
              Brick Working Report – {new Date().getFullYear()}
            </TableCell>

            {/* Quarter headers - show all if none expanded, or only the expanded one */}
            {expandedQuarter ? (
              <TableCell colSpan={7} sx={groupHeader}>
                {expandedQuarter} ({getQuarterRange(expandedQuarter)})
              </TableCell>
            ) : (
              quarters.map((q, idx) => (
                <TableCell
                  key={q}
                  colSpan={4}
                  sx={{
                    ...groupHeader,
                    borderRight:
                      idx < quarters.length - 1 ? "2px solid #fff" : "none",
                  }}
                >
                  {q} ({getQuarterRange(q)})
                </TableCell>
              ))
            )}
          </TableRow>

          {/* COLUMN HEADER */}
          <TableRow>
            <TableCell sx={columnHeader}>
              <Box display="flex" alignItems="center">
                Area
                <IconButton size="small" onClick={() => setOpenFilter("area")}>
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              </Box>
            </TableCell>

            <TableCell sx={columnHeader}>
              <Box display="flex" alignItems="center">
                Headquarter
                <IconButton
                  size="small"
                  onClick={() => setOpenFilter("headquarter")}
                >
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              </Box>
            </TableCell>
            <TableCell sx={{ ...columnHeader, ...stickyBrickOnly }}>
              <Box display="flex" alignItems="center">
                Brick
                <IconButton size="small" onClick={() => setOpenFilter("brick")}>
                  <FilterAltIcon fontSize="small" />
                </IconButton>
              </Box>
            </TableCell>
            <TableCell sx={{ ...columnHeader, ...quarterBorderRight }}>
              Brick Potential
            </TableCell>

            {/* Quarter columns - show all if none expanded, or only the expanded one */}
            {expandedQuarter ? (
              <>
                <TableCell
                  sx={{ ...columnHeader, ...(expandedQuarter && gridCell) }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Customer Met
                    {/* <IconButton
                      size="small"
                      onClick={() => toggleQuarter(expandedQuarter)}
                    >
                      <ExpandLessIcon fontSize="small" />
                    </IconButton> */}
                  </Box>
                </TableCell>

                {/* MONTHS */}
                {/* {getMonthNames(expandedQuarter).map((month, idx, arr) => (
    <TableCell key={month} sx={columnHeader} >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={0.5}
      >
        {month}

        {idx === arr.length - 1 && (
          <IconButton
            size="small"
            onClick={() => toggleQuarter(expandedQuarter)}
              sx={circleIconButton}
          >
            <ChevronLeftIcon  fontSize="small" />
          </IconButton>
        )}
      </Box>
    </TableCell>
  ))} */}

                {getMonthNames(expandedQuarter).map((month, idx, arr) => (
                  <TableCell
                    key={month}
                    sx={{
                      ...columnHeader,
                      width: MONTH_COL_WIDTH,
                      minWidth: MONTH_COL_WIDTH,
                      maxWidth: MONTH_COL_WIDTH,
                      ...(expandedQuarter && gridCell),
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={0.5}
                    >
                      {month}

                      {idx === arr.length - 1 && (
                        <IconButton
                          size="small"
                          onClick={() => toggleQuarter(expandedQuarter)}
                          sx={circleIconButton}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                ))}

                <TableCell
                  sx={{ ...columnHeader, ...(expandedQuarter && gridCell) }}
                >
                  ACPs
                </TableCell>
                <TableCell
                  sx={{ ...columnHeader, ...(expandedQuarter && gridCell) }}
                >
                  Secondary Orders
                </TableCell>
                <TableCell
                  sx={{ ...columnHeader, ...(expandedQuarter && gridCell) }}
                >
                  Invoices
                </TableCell>
              </>
            ) : (
              quarters.map((q, idx) => (
                <React.Fragment key={q}>
                  <TableCell sx={columnHeader}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Customer Met
                      <IconButton
                        size="small"
                        onClick={() => toggleQuarter(q)}
                        sx={circleIconButton}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={columnHeader}>ACPs</TableCell>
                  <TableCell sx={columnHeader}>Secondary Orders</TableCell>
                  <TableCell
                    sx={{
                      ...columnHeader,
                      ...(idx < quarters.length - 1 ? quarterBorderRight : {}),
                    }}
                  >
                    Invoices
                  </TableCell>
                </React.Fragment>
              ))
            )}
          </TableRow>
        </TableHead>

        {/* ================= BODY ================= */}
        <TableBody>
          {loading && (
            <>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonRow key={i} columns={expandedQuarter ? 11 : 20} />
              ))}
            </>
          )}
          {!loading &&
            filteredRows.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell>{row.area}</TableCell>
                <TableCell>{row.headquarter}</TableCell>
                <TableCell sx={stickyBrickOnly}>{row.brick}</TableCell>
                <TableCell sx={quarterBorderRight}>
                  {formatAmount(row.brickPotential)}
                </TableCell>

                {expandedQuarter ? (
                  // Show only the expanded quarter
                  <>
                    <TableCell
                      sx={{
                        width: MONTH_COL_WIDTH,
                        minWidth: MONTH_COL_WIDTH,
                        maxWidth: MONTH_COL_WIDTH,
                        whiteSpace: "normal",
                        wordBreak: "break-word", 
                        lineHeight: 1.4,
                        ...(expandedQuarter && gridCell),
                      }}
                    >
                      {
                        row[
                          expandedQuarter.toLowerCase() as
                            | "q1"
                            | "q2"
                            | "q3"
                            | "q4"
                        ].totalVisits
                      }
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(expandedQuarter && {
                          ...gridCell,
                          ...gridCellStrongRight,
                          // ...compactMonthCell,
                        }),
                      }}
                    >
                      {row[
                        expandedQuarter.toLowerCase() as
                          | "q1"
                          | "q2"
                          | "q3"
                          | "q4"
                      ].months?.[0]?.join(", ") || "–"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(expandedQuarter && {
                          ...gridCell,
                          ...gridCellStrongRight,
                        }),
                      }}
                    >
                      {row[
                        expandedQuarter.toLowerCase() as
                          | "q1"
                          | "q2"
                          | "q3"
                          | "q4"
                      ].months?.[1]?.join(", ") || "–"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(expandedQuarter && {
                          ...gridCell,
                          ...gridCellStrongRight,
                        }),
                      }}
                    >
                      {row[
                        expandedQuarter.toLowerCase() as
                          | "q1"
                          | "q2"
                          | "q3"
                          | "q4"
                      ].months?.[2]?.join(", ") || "–"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(expandedQuarter && {
                          ...gridCell,
                          ...gridCellStrongRight,
                        }),
                      }}
                    >
                      {
                        row[
                          expandedQuarter.toLowerCase() as
                            | "q1"
                            | "q2"
                            | "q3"
                            | "q4"
                        ].acps
                      }
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(expandedQuarter && {
                          ...gridCell,
                          ...gridCellStrongRight,
                        }),
                      }}
                    >
                      {
                        row[
                          expandedQuarter.toLowerCase() as
                            | "q1"
                            | "q2"
                            | "q3"
                            | "q4"
                        ].secondaryOrders
                      }
                    </TableCell>
                    <TableCell
                      sx={{
                        ...(expandedQuarter && {
                          ...gridCell,
                          ...gridCellStrongRight,
                        }),
                      }}
                    >
                      {
                        row[
                          expandedQuarter.toLowerCase() as
                            | "q1"
                            | "q2"
                            | "q3"
                            | "q4"
                        ].invoices
                      }
                    </TableCell>
                  </>
                ) : (
                  // Show all quarters
                  <>
                    {/* Q1 DATA */}
                    <TableCell>{row.q1.totalVisits}</TableCell>
                    <TableCell>{row.q1.acps}</TableCell>
                    <TableCell>
                      {formatAmount(row.q1.secondaryOrders)}
                    </TableCell>
                    <TableCell sx={quarterBorderRight}>
                      {row.q1.invoices}
                    </TableCell>

                    {/* Q2 DATA */}
                    <TableCell>{row.q2.totalVisits}</TableCell>
                    <TableCell>{row.q2.acps}</TableCell>
                    <TableCell>
                      {formatAmount(row.q2.secondaryOrders)}
                    </TableCell>
                    <TableCell sx={quarterBorderRight}>
                      {row.q2.invoices}
                    </TableCell>

                    {/* Q3 DATA */}
                    <TableCell>{row.q3.totalVisits}</TableCell>
                    <TableCell>{row.q3.acps}</TableCell>
                    <TableCell>
                      {formatAmount(row.q3.secondaryOrders)}
                    </TableCell>
                    <TableCell sx={quarterBorderRight}>
                      {row.q3.invoices}
                    </TableCell>

                    {/* Q4 DATA */}
                    <TableCell>{row.q4.totalVisits}</TableCell>
                    <TableCell>{row.q4.acps}</TableCell>
                    <TableCell>
                      {formatAmount(row.q4.secondaryOrders)}
                    </TableCell>
                    <TableCell>{row.q4.invoices}</TableCell>
                  </>
                )}
              </TableRow>
            ))}

          {openFilter && (
            <BrickAreaFilter
              openFilter={Boolean(openFilter)}
              filterKey={openFilter}
              title={`Select ${openFilter}`}
              uniqueList={(key) =>
                Array.from(new Set(allRows.map((r) => r[key])))
              }
              selectedValues={columnFilters[openFilter]}
              handleFilter={() => setOpenFilter(null)}
              handleSelectedOptions={(selected: string[]) => {
                onApplyFilter(openFilter, selected);
                setOpenFilter(null);
              }}
            />
          )}
          {!loading && filteredRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={20} align="center">
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BrickWorkingReportExpandableTable;

// ================= STYLES =================
const groupHeader = {
  backgroundColor: "#1f6f9c",
  color: "#fff",
  fontWeight: 600,
  textAlign: "center",
};

const groupHeaderEmpty = {
  backgroundColor: "#1f6f9c",
};

const columnHeader = {
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const quarterBorderRight = {
  borderRight: "2px solid #e0e0e0",
};

const stickyBrickOnly = {
  position: "sticky",
  left: 0,
  zIndex: 6,
  backgroundColor: "#fff",
  boxShadow: "2px 0 6px rgba(0,0,0,0.12)",

  width: BRICK_COL_WIDTH,
  minWidth: BRICK_COL_WIDTH,
  maxWidth: BRICK_COL_WIDTH,

  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.4,
};

const circleIconButton = {
  backgroundColor: "#1f6f9c",
  color: "#fff",
  borderRadius: "50%",
  width: 18,
  height: 18,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: "#175a7c",
  },
};

const gridCell = {
  border: "1px solid #e0e0e0",
};

const gridCellStrongRight = {
  borderRight: "2px solid #c5c5c5",
};

const gridCellStrongBottom = {
  borderBottom: "2px solid #c5c5c5",
};

const compactMonthCell = {
  maxHeight: 36, 
  padding: "4px 8px", 
  fontSize: "12px",
  lineHeight: "16px",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2, 
  WebkitBoxOrient: "vertical",
  textOverflow: "ellipsis",
};
